import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { LuMapPin, LuClock, LuPhone, LuMail } from 'react-icons/lu';
import { useLanguage } from '../../contexts/LanguageContext';
import { checkPharmacyStatus, getBusinessHours } from '../../utils/businessHours';
import { GUTTER, PANEL_GAP, RED_SHADOW, RedSurfaceDecor, Eyebrow, PillButton } from './landingUi';

const PHONE_DISPLAY = '+41 61 691 74 88';
const PHONE_HREF = 'tel:+41616917488';
const EMAIL = 'info@dreirosenapotheke.ch';

const Map = () => {
  const { t } = useLanguage();
  // Dreirosen Apotheke coordinates
  const latitude = 47.5688988;
  const longitude = 7.5906717;
  
  // Create OpenStreetMap URL (no attribution required)
  const mapUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${longitude-0.01},${latitude-0.01},${longitude+0.01},${latitude+0.01}&layer=mapnik&marker=${latitude},${longitude}`;

  // Live open/closed status, re-checked every minute (same source as Banner).
  const [status, setStatus] = useState(() => checkPharmacyStatus(t));
  useEffect(() => {
    setStatus(checkPharmacyStatus(t));
    const id = setInterval(() => setStatus(checkPharmacyStatus(t)), 60000);
    return () => clearInterval(id);
  }, [t]);

  const directionsHref = `https://www.openstreetmap.org/directions?from=&to=${latitude},${longitude}`;
  const contactRows = [
    { Icon: LuMapPin, label: t('home.map.location') },
    { Icon: LuClock, label: getBusinessHours(t) },
    { Icon: LuPhone, label: PHONE_DISPLAY, href: PHONE_HREF },
    { Icon: LuMail, label: EMAIL, href: `mailto:${EMAIL}` },
  ];

  return (
    <>
    {/* Red contact card + map: side by side (4/8) on desktop, stacked on
        phones with the card first — on a phone the address, hours and a
        call button matter more than the map. */}
    <section className={`${GUTTER} ${PANEL_GAP}`} aria-label="Location">
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-12 lg:gap-[clamp(0.75rem,1.2vw,1.25rem)]">
        <motion.div
          className={`relative flex flex-col justify-between overflow-hidden rounded-[2rem] bg-brand p-[clamp(1.5rem,5vw,3rem)] text-white lg:col-span-4 lg:min-h-[30rem] lg:rounded-[2.5rem] lg:p-[clamp(1.75rem,2.6vw,3rem)] ${RED_SHADOW}`}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          <RedSurfaceDecor radius="rounded-[2rem] lg:rounded-[2.5rem]" />
          <div className="relative">
            <Eyebrow tone="onRed" pulse={status.isOpen}>{status.status}</Eyebrow>
            <h2 className="mt-5 text-[clamp(1.9rem,8vw,3rem)] font-extrabold lg:mt-6 lg:text-[clamp(2rem,2.8vw,3rem)] leading-[1] tracking-[-0.04em]">
              {t('home.map.title')}
            </h2>
          </div>

          <ul className="relative mt-6 space-y-3 lg:mt-8">
            {contactRows.map(({ Icon, label, href }) => {
              const body = (
                <>
                  <span className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white/15 ring-1 ring-inset ring-white/20 transition-colors duration-200 group-hover/row:bg-white group-hover/row:text-brand" aria-hidden="true">
                    <Icon className="h-4 w-4" strokeWidth={2} />
                  </span>
                  <span className="text-[14px] font-medium text-white/90">{label}</span>
                </>
              );
              return (
                <li key={label}>
                  {href ? (
                    <a href={href} className="group/row flex items-center gap-3 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70">{body}</a>
                  ) : (
                    <div className="group/row flex items-center gap-3">{body}</div>
                  )}
                </li>
              );
            })}
          </ul>

          <div className="relative mt-7 grid grid-cols-2 gap-2.5 sm:flex sm:flex-wrap sm:gap-3 lg:mt-8">
            <PillButton variant="white" href={directionsHref} target="_blank" rel="noopener noreferrer" className="justify-center">
              {t('home.map.getDirections')}
            </PillButton>
            <PillButton variant="glass" arrow={false} className="justify-center" href={PHONE_HREF} aria-label={`${t('home.map.callUs')}: ${PHONE_DISPLAY}`}>
              {t('home.map.callUs')}
            </PillButton>
          </div>
        </motion.div>

        <motion.div
          className="relative h-[20rem] overflow-hidden rounded-[2rem] bg-[#F6F3F0] sm:h-[24rem] lg:col-span-8 lg:h-auto lg:min-h-[30rem] lg:rounded-[2.5rem] ring-1 ring-black/[0.05] shadow-[0_30px_70px_-40px_rgba(17,24,39,0.45)]"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.55, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Slightly desaturated so the map sits with the page palette
              rather than shouting in OSM's own colours. */}
          <iframe
            src={mapUrl}
            className="absolute inset-0 h-full w-full [filter:saturate(0.75)_contrast(1.02)]"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            title="Dreirosen Apotheke Location"
            aria-label={t('home.map.mapLabel')}
          />
        </motion.div>
      </div>
    </section>

    </>
  );
};

export default Map;
